import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { useAuth } from "@/context/AuthContext";
import CustomerCard from "@/components/CustomerCard";
import EditCustomerDialog from "@/components/EditCustomerDialog";
import DeleteCustomersDialog from "@/components/DeleteCustomersDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Search, UserRound, AlertCircle, Upload, FileSpreadsheet, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const { meta } = useMeta();
  const { user } = useAuth();
  const qc = useQueryClient();
  const canFilter = user.role === "admin" || user.role === "manager";
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("active");
  const [assigned, setAssigned] = useState("all");
  const [group, setGroup] = useState("all");
  const [focus, setFocus] = useState("all"); // all | uncontacted | stale
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { setSelected(new Set()); }, [search, type, status, assigned, group, focus]);

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const { data: team = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: canFilter,
  });
  const salespeople = team.filter((u) => u.role === "executive" || u.role === "manager");

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const idleDays = settings?.idle_days ?? 7;

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers", search, type, status, assigned, group],
    queryFn: async () => {
      const params = { search, status, sort: "focus" };
      if (type !== "all") params.customer_type = type;
      if (canFilter && assigned !== "all") params.assigned_to = assigned;
      if (group !== "all") params.stage = group;
      return (await api.get("/customers", { params })).data;
    },
  });

  const isActive = status === "active";
  const notContacted = customers.filter((c) => c.never_contacted);
  const staleList = customers.filter((c) => !c.never_contacted && (c.days_since_message ?? 0) >= idleDays);
  const shown = focus === "uncontacted" ? notContacted : focus === "stale" ? staleList : customers;
  const showFocusBar = isActive && !isLoading && (notContacted.length > 0 || staleList.length > 0);
  const selectedShown = shown.filter((c) => selected.has(c.id));

  return (
    <div className="animate-fade-up">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">Customers</h1>
          <p className="mt-1 text-sm text-stone-500">{focus !== "all" ? `${shown.length} of ${customers.length}` : customers.length} records</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-customers-button">
            <Upload className="mr-1.5 h-4 w-4" /> Import
          </Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={() => setAddOpen(true)} data-testid="add-customer-button">
            <Plus className="mr-1.5 h-4 w-4" /> Add
          </Button>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-2 lg:flex-row lg:flex-wrap">
        <div className="relative flex-1 lg:min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company or mobile"
            className="pl-9 bg-white"
            data-testid="customer-search-input"
          />
        </div>
        {canFilter && (
          <Select value={assigned} onValueChange={setAssigned}>
            <SelectTrigger className="w-full lg:w-52 bg-white" data-testid="customer-assigned-filter"><SelectValue placeholder="Salesperson" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All salespeople</SelectItem>
              {salespeople.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} · {u.role === "manager" ? "Manager" : "Executive"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger className="w-full lg:w-56 bg-white" data-testid="customer-group-filter"><SelectValue placeholder="Group / stage" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All groups</SelectItem>
            {(meta?.stages || []).map((s) => (
              <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-full lg:w-40 bg-white" data-testid="customer-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="prospect">Prospects</SelectItem>
            <SelectItem value="existing">Existing</SelectItem>
          </SelectContent>
        </Select>
        <div className="inline-flex rounded-lg border border-stone-200 bg-white p-0.5" data-testid="customer-status-filter">
          {[["active", "Active"], ["dormant", "Dormant"], ["archived", "Archived"]].map(([val, label]) => (
            <button
              key={val}
              onClick={() => { setStatus(val); setFocus("all"); }}
              data-testid={`customer-status-${val}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                status === val ? "bg-forest text-ivory" : "text-stone-500 hover:text-forest"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {showFocusBar && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="focus-bar">
          <span className="text-xs font-semibold uppercase tracking-wide text-stone-400">Focus on idle</span>
          <FocusChip tone="amber" count={notContacted.length} label="not contacted"
            active={focus === "uncontacted"} testid="focus-chip-uncontacted"
            onClick={() => setFocus(focus === "uncontacted" ? "all" : "uncontacted")} />
          <FocusChip tone="orange" count={staleList.length} label={`no contact ${idleDays}d+`}
            active={focus === "stale"} testid="focus-chip-stale"
            onClick={() => setFocus(focus === "stale" ? "all" : "stale")} />
          {focus !== "all" && (
            <button onClick={() => setFocus("all")} className="text-xs font-medium text-stone-500 underline hover:text-forest" data-testid="focus-chip-clear">
              Show all
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
      ) : customers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white/50 py-16 text-center">
          <UserRound className="mx-auto h-10 w-10 text-stone-300" />
          <p className="mt-3 text-sm text-stone-500">No {isActive ? "active" : status} customers found.{isActive ? " Add your first customer to begin." : ""}</p>
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white/50 py-14 text-center text-sm text-stone-500" data-testid="focus-empty">
          No customers match this focus filter right now.
        </div>
      ) : (
        <>
          <SelectionBar
            total={shown.length}
            count={selectedShown.length}
            onSelectAll={(all) => setSelected(all ? new Set(shown.map((c) => c.id)) : new Set())}
            onDelete={() => setToDelete(selectedShown)}
          />
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {shown.map((c) => (
              <CustomerCard
                key={c.id} customer={c} onEdit={setEditCustomer} onDelete={(x) => setToDelete([x])}
                selected={selected.has(c.id)} onToggleSelect={toggleSelect}
                highlightIdle={isActive} idleDays={idleDays}
              />
            ))}
          </div>
        </>
      )}

      {toDelete && (
        <DeleteCustomersDialog
          open
          customers={toDelete}
          onOpenChange={(o) => { if (!o) setToDelete(null); }}
          onDeleted={() => { setToDelete(null); setSelected(new Set()); }}
        />
      )}

      <AddCustomerDialog open={addOpen} onOpenChange={setAddOpen} meta={meta} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
      {editCustomer && (
        <EditCustomerDialog
          open
          onOpenChange={(o) => { if (!o) setEditCustomer(null); }}
          customer={editCustomer}
          meta={meta}
          onSaved={() => { setEditCustomer(null); qc.invalidateQueries({ queryKey: ["customers"] }); }}
        />
      )}
    </div>
  );
}

function SelectionBar({ total, count, onSelectAll, onDelete }) {
  const all = count > 0 && count === total;
  return (
    <div className={`mb-3 flex min-h-[44px] flex-wrap items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${count ? "border-red-200 bg-red-50/60" : "border-transparent"}`} data-testid="customer-selection-bar">
      <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-stone-600">
        <Checkbox
          checked={all ? true : count > 0 ? "indeterminate" : false}
          onCheckedChange={() => onSelectAll(!all)}
          data-testid="customer-select-all"
          className="rounded-[4px] border-stone-300 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 data-[state=indeterminate]:bg-red-600 data-[state=indeterminate]:border-red-600 data-[state=indeterminate]:text-white"
        />
        {count ? <span className="font-semibold text-stone-800" data-testid="customer-selected-count">{count} selected</span> : `Select all (${total})`}
      </label>
      {count > 0 && (
        <>
          <button onClick={() => onSelectAll(false)} className="text-xs font-medium text-stone-500 underline hover:text-stone-800" data-testid="customer-clear-selection">Clear</button>
          <Button size="sm" className="ml-auto bg-red-600 text-white hover:bg-red-700" onClick={onDelete} data-testid="customer-bulk-delete-button">
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete selected ({count})
          </Button>
        </>
      )}
    </div>
  );
}

function FocusChip({ active, onClick, tone, count, label, testid }) {
  const tones = {
    amber: active
      ? "bg-amber-500 text-white border-amber-500"
      : "bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400",
    orange: active
      ? "bg-orange-500 text-white border-orange-500"
      : "bg-orange-50 text-orange-700 border-orange-200 hover:border-orange-400",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${tones[tone]}`}
    >
      <span className="font-bold">{count}</span> {label}
    </button>
  );
}

function AddCustomerDialog({ open, onOpenChange, meta }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const empty = { name: "", country_code: "+91", mobile: "", company: "", customer_type: "prospect", preferred_language: "hinglish" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [dup, setDup] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (force = false) => {
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile number are required");
      return;
    }
    setLoading(true);
    if (!force) setDup(null);
    try {
      const r = await api.post("/customers", { ...form, force });
      toast.success(force ? "Duplicate authorized and added" : "Customer created");
      qc.invalidateQueries({ queryKey: ["customers"] });
      onOpenChange(false);
      setForm(empty);
      navigate(`/customers/${r.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.customer) {
        setDup(detail.customer);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setDup(null); setForm(empty); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Add customer</DialogTitle>
          <DialogDescription>Only a name and mobile number are required. You can add the rest later.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Customer name *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Suresh Traders" data-testid="add-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Mobile number *</Label>
            <div className="flex gap-2">
              <Input value={form.country_code} onChange={(e) => set("country_code", e.target.value)} className="w-20" data-testid="add-country-code-input" />
              <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="98765 43210" className="flex-1" data-testid="add-mobile-input" inputMode="tel" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Company (optional)</Label>
            <Input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company name" data-testid="add-company-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.customer_type} onValueChange={(v) => set("customer_type", v)}>
                <SelectTrigger data-testid="add-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospect">Prospect</SelectItem>
                  <SelectItem value="existing">Existing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Language</Label>
              <Select value={form.preferred_language} onValueChange={(v) => set("preferred_language", v)}>
                <SelectTrigger data-testid="add-language-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.languages || []).map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}{l.recommended ? " ★" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {dup && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="duplicate-warning">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertCircle className="h-4 w-4" /> This mobile number already exists
              </div>
              <p className="mt-1 text-xs text-amber-700">{dup.name}{dup.company ? ` · ${dup.company}` : ""} — {dup.mobile}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  size="sm" variant="outline"
                  className="border-amber-400 text-amber-800"
                  onClick={() => { onOpenChange(false); navigate(`/customers/${dup.id}`); }}
                  data-testid="open-existing-customer-button"
                >
                  Open existing record
                </Button>
                {user.role === "admin" ? (
                  <Button
                    size="sm" variant="destructive"
                    onClick={() => submit(true)} disabled={loading}
                    data-testid="authorize-duplicate-button"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Authorize & add anyway"}
                  </Button>
                ) : (
                  <span className="self-center text-xs text-amber-700">Only an administrator can authorize a duplicate.</span>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={() => submit(false)} disabled={loading || !!dup} data-testid="add-customer-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = user.role === "admin" || user.role === "manager";

  const [step, setStep] = useState("select"); // select | map | done
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {headers, sample, total_rows, fields}
  const [mapping, setMapping] = useState({});
  const [assignTo, setAssignTo] = useState(user.id);
  const [result, setResult] = useState(null);

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: isManager && open,
  });

  const reset = () => {
    setStep("select"); setFile(null); setFileName(""); setPreview(null);
    setMapping({}); setResult(null); setAssignTo(user.id);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setFileName(f ? f.name : "");
    setResult(null);
  };

  const downloadSample = async () => {
    try {
      const r = await api.get("/customers/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "pgt_customer_import_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the template");
    }
  };

  const doPreview = async () => {
    if (!file) { toast.error("Please choose a .xlsx or .csv file"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/customers/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setMapping(r.data.suggested_mapping || {});
      setStep("map");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const setMap = (field, header) => setMapping((m) => ({ ...m, [field]: header === "__none__" ? "" : header }));

  const doImport = async () => {
    if (!mapping.name || !mapping.mobile) { toast.error("Please map both Name and Mobile columns"); return; }
    if (!preview?.total_rows) { toast.error("This file has no data rows to import"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      if (isManager && assignTo) fd.append("assign_to", assignTo);
      const r = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      setStep("done");
      toast.success(`${r.data.created} customer(s) imported`);
      qc.invalidateQueries({ queryKey: ["customers"] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-forest" /> Import prospects & customers</DialogTitle>
          <DialogDescription>
            {step === "select" && "Upload an Excel (.xlsx) or CSV file. You'll map the columns on the next step."}
            {step === "map" && `Map your spreadsheet columns to the fields. ${preview?.total_rows || 0} rows detected.`}
            {step === "done" && "Import finished."}
          </DialogDescription>
        </DialogHeader>

        {step === "select" && (
          <div className="space-y-3">
            <button onClick={downloadSample} className="text-xs font-medium text-forest hover:underline" data-testid="import-download-template">Download Excel template (.xlsx)</button>
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 py-8 text-center hover:border-forest/40">
              <Upload className="h-7 w-7 text-stone-400" />
              <span className="text-sm text-stone-600">{fileName || "Click to choose a .xlsx or .csv file"}</span>
              <input type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
            </label>
            {isManager && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-stone-500">Assign imported customers to</Label>
                <Select value={assignTo} onValueChange={setAssignTo}>
                  <SelectTrigger data-testid="import-assign-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={user.id}>Me ({user.name})</SelectItem>
                    {users.filter((u) => u.active && u.id !== user.id).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {step === "map" && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {preview.fields.map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-[11px] font-medium text-stone-500">{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
                  <Select value={mapping[f.key] || "__none__"} onValueChange={(v) => setMap(f.key, v)}>
                    <SelectTrigger className="h-9" data-testid={`import-map-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— none —</SelectItem>
                      {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-stone-400">Preview (first {Math.min(preview.sample.length, 5)} rows)</div>
              <div className="overflow-x-auto rounded-lg border border-stone-200">
                <table className="w-full text-xs" data-testid="import-preview-table">
                  <thead className="bg-stone-50 text-left text-stone-400">
                    <tr>{preview.headers.map((h) => <th key={h} className="px-2 py-1.5 whitespace-nowrap">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((row, i) => (
                      <tr key={i} className="border-t border-stone-100">
                        {preview.headers.map((h) => <td key={h} className="px-2 py-1.5 whitespace-nowrap text-stone-600">{row[h]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="rounded-lg border border-stone-200 bg-white p-3 text-sm" data-testid="import-result">
            <div className="flex items-center gap-2 font-semibold text-forest"><CheckCircle2 className="h-4 w-4" /> {result.created} imported → {result.assigned_to_name}</div>
            {result.duplicates?.length > 0 && (
              <div className="mt-1.5 text-xs text-amber-700">{result.duplicates.length} duplicate number(s) skipped: {result.duplicates.map((d) => d.name).join(", ")}</div>
            )}
            {result.errors?.length > 0 && (
              <div className="mt-1.5 text-xs text-red-600">{result.errors.length} row(s) with errors (e.g. row {result.errors[0].row}: {result.errors[0].error})</div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "select" && (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={doPreview} disabled={loading || !file} data-testid="import-preview-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview & map"}
              </Button>
            </>
          )}
          {step === "map" && (
            <>
              <Button variant="ghost" onClick={() => setStep("select")}>Back</Button>
              <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={doImport} disabled={loading} data-testid="import-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${preview?.total_rows || 0} rows`}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={() => onOpenChange(false)} data-testid="import-done-button">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
